import { useEffect, useRef, useState } from "react";
import { Upload, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { usePortal } from "@/context/PortalContext";
import { useI18n } from "@/context/I18nContext";
import { term } from "@/lib/roles";
import { Card, Btn, CategoryIcon } from "@/components/portal/ui";
import { StatusBadge } from "@/components/portal/StatusBadge";
import { DynamicFields } from "@/components/portal/DynamicFields";

const field = "w-full px-3.5 h-11 text-sm border outline-none";

export function ProfileEditor() {
  const { categories, locations, config } = usePortal();
  const { t, L, lang } = useI18n();
  const [prof, setProf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const coverRef = useRef(null);
  const galleryRef = useRef(null);
  const [form, setForm] = useState({
    business_name: "", category_ids: [], location_id: "", description: {},
    contact_phone: "", website: "", cover_image: "", gallery: [], fields: {},
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/providers/me");
        if (data) {
          setProf(data);
          setForm({
            business_name: data.business_name || "", category_ids: data.category_ids || [],
            location_id: data.location_id || "", description: data.description || {},
            contact_phone: data.contact_phone || "", website: data.website || "",
            cover_image: data.cover_image || "", gallery: data.gallery || [], fields: data.fields || {},
          });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleCat = (id) =>
    setForm((f) => ({
      ...f,
      category_ids: f.category_ids.includes(id) ? f.category_ids.filter((x) => x !== id) : [...f.category_ids, id],
    }));

  const doUpload = async (file, kind) => {
    if (!file) return;
    setUploading(kind);
    try {
      const url = await uploadFile(file);
      if (kind === "cover") setForm((f) => ({ ...f, cover_image: url }));
      else setForm((f) => ({ ...f, gallery: [...f.gallery, url] }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (prof) await api.put(`/providers/${prof.id}`, form);
      else {
        const { data } = await api.post("/providers", form);
        setProf(data);
      }
      toast.success(t("common.saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  if (loading) return <div className="text-sm py-10" style={{ color: "var(--text-muted)" }}>{t("common.loading")}</div>;

  return (
    <Card className="p-6 max-w-2xl" data-testid="profile-editor">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-2xl font-bold" style={{ color: "var(--text)" }}>{t("dash.myProfile")}</h3>
        {prof && (
          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--text-muted)" }}>
            {t("profile.status")}: <StatusBadge status={prof.status} />
          </div>
        )}
      </div>
      <div className="space-y-4">
        {/* cover */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.cover")}</label>
          <div className="mt-2 flex items-center gap-3">
            <div className="h-20 w-32 overflow-hidden border shrink-0 flex items-center justify-center" style={{ borderColor: "var(--border)", borderRadius: "var(--radius)", background: "var(--surface-alt)" }}>
              {form.cover_image ? <img src={form.cover_image} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-6 w-6" style={{ color: "var(--text-muted)" }} />}
            </div>
            <input ref={coverRef} type="file" accept="image/*" hidden onChange={(e) => doUpload(e.target.files[0], "cover")} data-testid="cover-file" />
            <Btn variant="soft" onClick={() => coverRef.current?.click()} disabled={uploading === "cover"} data-testid="cover-upload">
              <Upload className="h-4 w-4" /> {uploading === "cover" ? t("media.uploading") : t("media.uploadCover")}
            </Btn>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.businessName")}</label>
          <input data-testid="profile-name" className={field} style={st} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{term(config, "categories", L, t("profile.categories"))}</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {categories.map((c) => {
              const on = form.category_ids.includes(c.id);
              return (
                <button key={c.id} data-testid={`profile-cat-${c.slug}`} onClick={() => toggleCat(c.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border"
                  style={{ borderRadius: "var(--radius)", borderColor: on ? "var(--accent)" : "var(--border)", background: on ? "var(--accent)" : "transparent", color: on ? "var(--accent-fg)" : "var(--text)" }}>
                  <CategoryIcon name={c.icon} className="h-3.5 w-3.5" /> {L(c.name)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.location")}</label>
            <select data-testid="profile-location" className={field} style={st} value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}>
              <option value="">—</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{L(l.name)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.phone")}</label>
            <input data-testid="profile-phone" className={field} style={st} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.description")} ({lang.toUpperCase()})</label>
          <textarea data-testid="profile-description" rows={4} className="w-full px-3.5 py-2.5 text-sm border outline-none" style={st}
            value={form.description[lang] || ""} onChange={(e) => setForm({ ...form, description: { ...form.description, [lang]: e.target.value } })} />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("profile.website")}</label>
          <input data-testid="profile-website" className={field} style={st} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        </div>

        {/* gallery */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("dash.gallery")}</label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="gallery-grid">
            {form.gallery.map((url, i) => (
              <div key={i} className="relative h-20 w-20 overflow-hidden border group" style={{ borderColor: "var(--border)", borderRadius: "var(--radius)" }}>
                <img src={url} alt="" className="h-full w-full object-cover" />
                <button onClick={() => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, k) => k !== i) }))}
                  className="absolute top-1 right-1 h-5 w-5 flex items-center justify-center rounded-full" style={{ background: "#dc2626", color: "#fff" }} data-testid={`gallery-remove-${i}`}>
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <input ref={galleryRef} type="file" accept="image/*" hidden onChange={(e) => doUpload(e.target.files[0], "gallery")} data-testid="gallery-file" />
            <button onClick={() => galleryRef.current?.click()} disabled={uploading === "gallery"}
              className="h-20 w-20 flex flex-col items-center justify-center gap-1 border border-dashed text-xs" style={{ borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text-muted)" }} data-testid="gallery-upload">
              <ImagePlus className="h-5 w-5" /> {uploading === "gallery" ? "…" : "+"}
            </button>
          </div>
        </div>

        {config?.profile_fields?.length > 0 && (
          <DynamicFields fields={config.profile_fields} values={form.fields} onChange={(k, v) => setForm((f) => ({ ...f, fields: { ...f.fields, [k]: v } }))} />
        )}

        <Btn onClick={save} disabled={saving} data-testid="profile-save" className="w-full">
          {prof ? t("common.save") : t("profile.create")}
        </Btn>
      </div>
    </Card>
  );
}
